import type { ReactNode } from 'react'
import { useBreakpoint } from '../responsive/useBreakpoint'
import { useHitTarget } from '../responsive/hitTarget'
import './listRow.css'

type RowProps = {
  label: string
  sublabel?: string
  leading?: ReactNode
  trailing?: ReactNode
  /** Present makes the row a button. Absent leaves it inert — a row that
   *  does nothing must not be focusable, or the keyboard tour is full of
   *  stops that go nowhere. */
  onActivate?: () => void
  /** Only meaningful alongside `onActivate` — a plain div has no affordance to
   *  disable. `listRow.css` is a registered control (`controlTokens.test.ts`),
   *  which is what a real `<button>` skin is required to carry: a control that
   *  CAN be disabled must skin the state, even before a caller reaches for it. */
  disabled?: boolean
  /** A child row. Indentation is the only thing that differs — the row does
   *  not become a different component because it has a parent. */
  indented?: boolean
}

/**
 * `trailing` is always a sibling of the activation control, never inside it —
 * a category row that is both navigable (`onActivate`) and carries an edit
 * `trailing` button would otherwise put a `<button>` inside a `<button>`,
 * which is invalid HTML and collapses every `getByRole('button')` count.
 *
 * `minHeight` is applied inline on BOTH the outer div and, when `onActivate`
 * is present, the button itself — issue #43 review, finding 2. The div alone
 * reserves the floor as a box the row occupies; it does not make the box a
 * caller can tap ANYWHERE in it activate the row, because only the button
 * receives pointer/keyboard activation. Left off the button, the div could be
 * 44px tall on a coarse pointer while the button inside it — the actual hit
 * target — sat at its own, smaller content height, and a tap on the row's
 * padding would miss the control. The div's own copy stays so an INERT row
 * (no `onActivate`, so no button to carry the floor) still reserves the
 * documented row height for visual consistency across a list that mixes
 * active and inert rows.
 */
export function ListRow({
  label,
  sublabel,
  leading,
  trailing,
  onActivate,
  disabled,
  indented,
}: RowProps) {
  const minHeight = useHitTarget('panel')
  // M11-R21: stacked below 640px. `useBreakpoint()` — never a CSS media query
  // (`single-source.test.ts` forbids one in any stylesheet, no exemptions) —
  // is the one place this reads the viewport.
  //
  // ONE modifier class, never `list-row--stacked` alongside `list-row--indented`
  // together: `controlTokens.test.ts`'s row grammar admits only a single
  // `--modifier` per selector, and two classes on the same element competing
  // for `padding-inline-start` would tie on specificity and resolve by source
  // order rather than by which one is correct (#43). `listRow.css` carries the
  // stacked-and-indented combination as its own class for that reason.
  const stacked = useBreakpoint() === 'phone'
  const modifier = stacked
    ? indented
      ? 'list-row--stacked-indented'
      : 'list-row--stacked'
    : indented
      ? 'list-row--indented'
      : undefined
  const className = ['list-row', modifier].filter(Boolean).join(' ')

  const main = (
    <>
      {leading}
      <span className="list-row__text">
        <span className="list-row__label">{label}</span>
        {sublabel && <span className="list-row__sublabel">{sublabel}</span>}
      </span>
    </>
  )

  return (
    <div className={className} style={{ minHeight }}>
      {onActivate ? (
        <button
          type="button"
          className="list-row__main hoverable"
          onClick={onActivate}
          disabled={disabled}
          style={{ minHeight }}
        >
          {main}
        </button>
      ) : (
        main
      )}
      {trailing}
    </div>
  )
}

export function SectionHeader({ title, count }: { title: string; count?: number }) {
  return (
    <div className="section-header">
      <h3 className="section-header__title">{title}</h3>
      {count !== undefined && <span className="section-header__count">{count}</span>}
    </div>
  )
}
